import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Loader2,
    LogOut,
    Building2,
    Users,
    ShieldCheck,
    ScrollText,
    Bot,
    ToggleLeft,
    ToggleRight,
    Search,
    ChevronRight,
    Activity,
    Layers,
    Star,
    MessageSquare,
    AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

export default function AdminConsole() {
    const { user, logout } = useApp();
    const nav = useNavigate();
    const [overview, setOverview] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [aiUsage, setAiUsage] = useState(null);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingFlag, setSavingFlag] = useState(null); // `${tid}-${module}`
    const [searchTenant, setSearchTenant] = useState("");
    const [searchUser, setSearchUser] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [ov, tn, us, lg, ai, mo] = await Promise.all([
                api.adminOverview(),
                api.adminTenants(),
                api.adminUsers(),
                api.adminAuditLog({ limit: 100 }),
                api.adminAiUsage({ limit: 100 }),
                api.adminModules(),
            ]);
            setOverview(ov);
            setTenants(tn.tenants || []);
            setUsers(us.users || []);
            setLogs(lg.logs || []);
            setAiUsage(ai);
            setModules(mo.modules || []);
        } catch (e) {
            toast.error("Couldn't load admin data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const toggleFlag = async (tenantId, module, enabled) => {
        setSavingFlag(`${tenantId}-${module}`);
        try {
            const { flags } = await api.adminSetFlag(tenantId, module, enabled);
            setTenants((prev) =>
                prev.map((t) =>
                    t.id === tenantId ? { ...t, modules: flags.modules } : t,
                ),
            );
            toast.success(`${module} → ${enabled ? "enabled" : "disabled"}`);
        } catch {
            toast.error("Update failed.");
        } finally {
            setSavingFlag(null);
        }
    };

    const filteredTenants = tenants.filter((t) =>
        t.name.toLowerCase().includes(searchTenant.toLowerCase()),
    );
    const filteredUsers = users.filter(
        (u) =>
            u.email.toLowerCase().includes(searchUser.toLowerCase()) ||
            (u.name || "").toLowerCase().includes(searchUser.toLowerCase()) ||
            (u.tenant_name || "").toLowerCase().includes(searchUser.toLowerCase()),
    );

    if (loading || !overview) {
        return (
            <div className="min-h-screen bg-[#090d16] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
        );
    }

    const initials = (user?.name || "SA")
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <div
            data-testid="admin-console-page"
            className="min-h-screen bg-[#090d16] grain"
        >
            <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0B0F19]/90 backdrop-blur-xl px-8 h-16 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-rose-500 via-fuchsia-400 to-amber-400 flex items-center justify-center text-slate-950 font-black text-lg">
                        A
                    </div>
                    <div>
                        <div className="font-extrabold text-slate-100 text-lg leading-none">
                            Reputation AI · Super Admin
                        </div>
                        <div className="label-eyebrow text-fuchsia-300 mt-1">
                            Metadata-only console — privacy-safe
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-rose-500/20 text-rose-100 text-xs font-semibold">
                            {initials}
                        </AvatarFallback>
                    </Avatar>
                    <div className="text-xs text-slate-400 mono">{user?.email}</div>
                    <Button
                        data-testid="admin-logout"
                        onClick={logout}
                        variant="outline"
                        className="h-8 gap-1 border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Sign out
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-8 py-8 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    <StatCard
                        testid="admin-stat-tenants"
                        label="Tenants"
                        value={overview.tenants}
                        icon={Building2}
                        color="indigo"
                    />
                    <StatCard
                        testid="admin-stat-users"
                        label="Users"
                        value={overview.users}
                        icon={Users}
                        color="cyan"
                    />
                    <StatCard
                        testid="admin-stat-businesses"
                        label="Businesses"
                        value={overview.businesses}
                        icon={Layers}
                        color="emerald"
                    />
                    <StatCard
                        testid="admin-stat-reviews"
                        label="Reviews (all tenants)"
                        value={overview.total_reviews}
                        icon={Star}
                        color="amber"
                    />
                    <StatCard
                        testid="admin-stat-comments"
                        label="Comments (all tenants)"
                        value={overview.total_comments}
                        icon={MessageSquare}
                        color="fuchsia"
                    />
                    <StatCard
                        testid="admin-stat-audits"
                        label="Audits run"
                        value={overview.audits_run}
                        icon={ShieldCheck}
                        color="indigo"
                    />
                    <StatCard
                        testid="admin-stat-approvals"
                        label="Approvals pending"
                        value={overview.approvals_pending}
                        icon={ScrollText}
                        color="rose"
                    />
                    <StatCard
                        testid="admin-stat-alerts"
                        label="Alerts open"
                        value={overview.alerts_open}
                        icon={AlertCircle}
                        color="rose"
                    />
                    <StatCard
                        testid="admin-stat-ai-msgs"
                        label="AI chat messages"
                        value={overview.ai_chat_messages}
                        icon={Bot}
                        color="cyan"
                    />
                    <StatCard
                        testid="admin-stat-ai-activity"
                        label="AI activity logs"
                        value={overview.ai_activity_logs}
                        icon={Activity}
                        color="emerald"
                    />
                </div>

                <Tabs defaultValue="tenants" className="w-full">
                    <TabsList className="bg-white/[0.03] border border-white/10">
                        <TabsTrigger value="tenants" data-testid="admin-tab-tenants">
                            Tenants · Feature Flags
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="admin-tab-users">
                            Users
                        </TabsTrigger>
                        <TabsTrigger value="audit" data-testid="admin-tab-audit">
                            Global Audit Log
                        </TabsTrigger>
                        <TabsTrigger value="ai" data-testid="admin-tab-ai">
                            AI Usage
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="tenants" className="mt-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <Search className="h-4 w-4 text-slate-500" />
                            <Input
                                data-testid="admin-search-tenants"
                                value={searchTenant}
                                onChange={(e) => setSearchTenant(e.target.value)}
                                placeholder="Search tenants..."
                                className="max-w-xs bg-white/[0.03] border-white/10 text-slate-100 h-9"
                            />
                        </div>
                        <div className="space-y-3">
                            {filteredTenants.map((t) => (
                                <div
                                    key={t.id}
                                    data-testid={`admin-tenant-${t.id}`}
                                    className="surface rounded-2xl p-5 space-y-4"
                                >
                                    <div className="flex items-start justify-between flex-wrap gap-3">
                                        <div>
                                            <div className="text-lg font-bold text-slate-100">
                                                {t.name}
                                            </div>
                                            <div className="text-xs text-slate-500 mono mt-1">
                                                {t.id} · {t.plan} · created{" "}
                                                {(t.created_at || "").slice(0, 10)}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-slate-400">
                                            <Badge
                                                variant="outline"
                                                className="border-white/10 bg-white/[0.03]"
                                            >
                                                {t.user_count} users
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className="border-white/10 bg-white/[0.03]"
                                            >
                                                {t.business_count} businesses
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className="border-white/10 bg-white/[0.03]"
                                            >
                                                {t.review_count} reviews
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-white/5">
                                        {modules.map((mod) => {
                                            const enabled = t.modules?.[mod] !== false;
                                            const busy = savingFlag === `${t.id}-${mod}`;
                                            return (
                                                <div
                                                    key={mod}
                                                    data-testid={`admin-flag-${t.id}-${mod}`}
                                                    className="flex items-center justify-between rounded-xl bg-white/[0.02] border border-white/5 px-3 py-2"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {enabled ? (
                                                            <ToggleRight className="h-3.5 w-3.5 text-emerald-300" />
                                                        ) : (
                                                            <ToggleLeft className="h-3.5 w-3.5 text-rose-300" />
                                                        )}
                                                        <span className="text-xs text-slate-200 capitalize">
                                                            {mod.replace("_", " ")}
                                                        </span>
                                                    </div>
                                                    {busy ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                                                    ) : (
                                                        <Switch
                                                            checked={enabled}
                                                            onCheckedChange={(v) =>
                                                                toggleFlag(t.id, mod, v)
                                                            }
                                                            data-testid={`admin-flag-switch-${t.id}-${mod}`}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="users" className="mt-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <Search className="h-4 w-4 text-slate-500" />
                            <Input
                                data-testid="admin-search-users"
                                value={searchUser}
                                onChange={(e) => setSearchUser(e.target.value)}
                                placeholder="Search users..."
                                className="max-w-xs bg-white/[0.03] border-white/10 text-slate-100 h-9"
                            />
                        </div>
                        <div className="surface rounded-2xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-white/[0.02] text-slate-400 mono uppercase text-[10px] tracking-widest">
                                    <tr>
                                        <th className="text-left px-4 py-3">Name</th>
                                        <th className="text-left px-4 py-3">Email</th>
                                        <th className="text-left px-4 py-3">Role</th>
                                        <th className="text-left px-4 py-3">Tenant</th>
                                        <th className="text-left px-4 py-3">Providers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((u) => (
                                        <tr
                                            key={u.id}
                                            data-testid={`admin-user-${u.id}`}
                                            className="border-t border-white/5"
                                        >
                                            <td className="px-4 py-3 text-slate-200">
                                                {u.name}
                                            </td>
                                            <td className="px-4 py-3 text-slate-300 mono text-xs">
                                                {u.email}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge
                                                    variant="outline"
                                                    className={
                                                        u.role === "super_admin"
                                                            ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
                                                            : "border-white/10 bg-white/[0.03] text-slate-300"
                                                    }
                                                >
                                                    {u.role}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-slate-300">
                                                {u.tenant_name}
                                            </td>
                                            <td className="px-4 py-3 text-slate-400 text-xs mono">
                                                {(u.auth_providers || []).join(", ") || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="audit" className="mt-6">
                        <div className="surface rounded-2xl overflow-hidden">
                            <div className="p-4 border-b border-white/5 text-xs text-slate-400">
                                Newest 100 entries · metadata sanitised — no raw review /
                                comment / message text
                            </div>
                            <ul className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                                {logs.map((l) => (
                                    <li
                                        key={l.id}
                                        data-testid={`admin-log-${l.id}`}
                                        className="px-4 py-2.5 flex items-start gap-3"
                                    >
                                        <Badge
                                            className={
                                                l.actor_kind === "ai"
                                                    ? "bg-indigo-500/15 border border-indigo-500/30 text-indigo-200"
                                                    : "bg-emerald-500/15 border border-emerald-500/30 text-emerald-200"
                                            }
                                        >
                                            {l.actor_kind}
                                        </Badge>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-slate-200 font-semibold">
                                                {l.action}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mono">
                                                {(l.created_at || "").slice(0, 19).replace("T", " ")}
                                                {" · "}
                                                tenant {l.tenant_id}
                                                {l.business_id
                                                    ? ` · biz ${l.business_id}`
                                                    : ""}
                                            </div>
                                            {l.metadata && Object.keys(l.metadata).length > 0 && (
                                                <div className="mt-1 text-[11px] text-slate-500 mono">
                                                    {JSON.stringify(l.metadata)}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </TabsContent>

                    <TabsContent value="ai" className="mt-6 space-y-4">
                        <div className="surface rounded-2xl p-5">
                            <div className="label-eyebrow text-indigo-300 mb-3">
                                AI action counts (metadata only)
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(aiUsage?.action_counts || []).map((a) => (
                                    <Badge
                                        key={a.action}
                                        data-testid={`admin-ai-count-${a.action}`}
                                        variant="outline"
                                        className="border-indigo-500/30 bg-indigo-500/10 text-indigo-100"
                                    >
                                        {a.action} · {a.count}
                                    </Badge>
                                ))}
                                {(!aiUsage?.action_counts ||
                                    aiUsage.action_counts.length === 0) && (
                                    <span className="text-xs text-slate-500">
                                        No AI activity yet.
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="surface rounded-2xl overflow-hidden">
                            <div className="p-4 border-b border-white/5 text-xs text-slate-400">
                                Recent AI activity · metadata-only
                            </div>
                            <ul className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
                                {(aiUsage?.recent || []).map((l) => (
                                    <li
                                        key={l.id}
                                        className="px-4 py-2.5 text-sm text-slate-200 flex items-center gap-3"
                                    >
                                        <Bot className="h-3.5 w-3.5 text-indigo-300" />
                                        <span className="font-semibold">{l.action}</span>
                                        <span className="text-[11px] text-slate-500 mono ml-auto">
                                            {(l.created_at || "").slice(0, 19).replace("T", " ")}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
            <Toaster richColors closeButton position="top-right" />
        </div>
    );
}

function StatCard({ testid, label, value, icon: Icon, color = "indigo" }) {
    const colors = {
        indigo: "text-indigo-300 border-indigo-500/30 bg-indigo-500/10",
        cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
        emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
        amber: "text-amber-300 border-amber-500/30 bg-amber-500/10",
        rose: "text-rose-300 border-rose-500/30 bg-rose-500/10",
        fuchsia: "text-fuchsia-300 border-fuchsia-500/30 bg-fuchsia-500/10",
    };
    return (
        <div
            data-testid={testid}
            className={`surface rounded-2xl p-4 border ${colors[color]}`}
        >
            <div className="flex items-center justify-between">
                <div className="label-eyebrow">{label}</div>
                <Icon className="h-4 w-4" />
            </div>
            <div className="mt-2 text-2xl font-extrabold text-slate-50">
                {value ?? "—"}
            </div>
        </div>
    );
}
